import { useCallback, useEffect, useRef, useState } from "react";
import {
  clearGridPosition,
  focusProductCard,
  MAX_RESTORE_REQUESTS,
  readGridPosition,
  restoreKeyFor,
  saveGridPosition,
  waitForProductCard,
} from "../../utils/categoryRestore";

// How long to keep watching the DOM for the clicked card. Generous, because the card can sit
// many pages down and each page is its own round trip to Algolia.
const CARD_WAIT_MS = 15000;

// Back-button restore for the infinite Algolia grids (category + search).
//
// The grid always remounts on page 0, so restoring means paging back in every page up to the one
// the shopper was on. Paging and scrolling run independently: showMore() is replayed until the
// saved page is reached, while the scroll fires the moment the saved card appears - usually
// before the replay has finished. New pages only append below, so the card stays put once centred.
//
// Paging is driven from here rather than left to DynamicLoader, whose observer can only page when
// the loader is on screen - during a replay it never is.
export function useGridScrollRestore({ page, isLastPage, status, showMore, hasHits }) {
  const keyRef = useRef(restoreKeyFor());
  const [entry] = useState(() => readGridPosition(keyRef.current));
  const [isRestoring, setIsRestoring] = useState(() => Boolean(entry));
  const scrollStartedRef = useRef(false);
  const requestedFromPageRef = useRef(-1);
  const requestCountRef = useRef(0);
  const stopWatchingRef = useRef(null);
  const pageRef = useRef(page);

  useEffect(() => {
    pageRef.current = page;
  });

  // Consumed as soon as it is read, so a stale entry can never outlive the navigation that armed
  // it - not even if the replay below is cut short by the shopper navigating away mid-restore.
  useEffect(() => {
    if (entry) clearGridPosition();
  }, [entry]);

  // Step 1 - replay pages, one request per settled page, until the saved page is back. The ref
  // keeps a re-render from firing a second showMore() for a page already asked to advance;
  // isLastPage and the request ceiling are only guards for a saved page that no longer exists.
  useEffect(() => {
    if (!isRestoring) return;
    if (status !== "idle" || !hasHits) return;
    if (page >= (entry?.page ?? 0) || isLastPage || requestCountRef.current >= MAX_RESTORE_REQUESTS) {
      setIsRestoring(false);
      return;
    }
    if (requestedFromPageRef.current === page) return;
    requestedFromPageRef.current = page;
    requestCountRef.current += 1;
    showMore();
  }, [isRestoring, status, hasHits, isLastPage, page, entry, showMore]);

  // Step 2 - put the card back on screen as soon as it exists, without waiting for the replay to
  // finish. Started once and then left alone: the watcher is torn down on unmount only, so the
  // status churn of the pages still loading underneath can't cancel it half way through.
  useEffect(() => () => stopWatchingRef.current?.(), []);

  useEffect(() => {
    if (!entry || scrollStartedRef.current) return;
    if (status !== "idle" || !hasHits) return;
    scrollStartedRef.current = true;

    let cancelScroll = () => {};
    const stopWaiting = waitForProductCard(
      entry.cardId,
      (el) => {
        cancelScroll = focusProductCard(el, entry.scrollY);
      },
      { timeout: CARD_WAIT_MS },
    );

    stopWatchingRef.current = () => {
      stopWaiting();
      cancelScroll();
    };
  }, [entry, status, hasHits]);

  // Attach to each card wrapper. Only real navigations are remembered - add-to-cart, quantity
  // steppers and the wishlist toggle all sit inside the same card and must not arm a restore.
  const onCardClickCapture = useCallback((event) => {
    const card = event.currentTarget;
    const cardId = card?.id;
    if (!cardId) return;
    const link = event.target.closest && event.target.closest("a[href]");
    if (!link || !card.contains(link)) return;
    saveGridPosition({ cardId, page: pageRef.current });
  }, []);

  return { isRestoring, onCardClickCapture };
}
